// Countries an e-paper subscriber can belong to. Managed from the E-Paper
// Settings screen and loaded from the backend by loadEpaperConfig()
// (config/epaperConfig.ts); FALLBACK_COUNTRIES only covers the moment before
// that first load. The backend is the source of truth for pricing; this list
// drives the forms.
export type Country = {
  id?: number;
  code: string;
  name: string;
  shortName: string;
  currency: string;
  currencyName: string;
  iso3: string;
  dialCode: string;
  defaultAmount: number;
  isActive: boolean;
  sortOrder: number;
  regionCode?: string | null; // E-Paper Settings region, lowercase
};

// Shape returned by GET epaper-settings/config and /countries.
export type CountryDto = {
  id: number;
  name: string;
  code: string;
  iso3: string | null;
  currency_name: string | null;
  currency_code: string;
  dial_code: string;
  default_amount: number;
  is_active: boolean;
  sort_order: number;
  region_id?: number | null;
  region_code?: string | null;
  region_name?: string | null;
  zone_count?: number;
};

const FALLBACK_COUNTRIES: Country[] = [
  { code: "AE", name: "UAE", currencyName: "UAE Dirham", currency: "AED", iso3: "ARE", dialCode: "+971", defaultAmount: 100 },
  { code: "SA", name: "Saudi Arabia", currencyName: "Saudi Riyal", currency: "SAR", iso3: "SAU", dialCode: "+966", defaultAmount: 50 },
  { code: "QA", name: "Qatar", currencyName: "Qatari Riyal", currency: "QAR", iso3: "QAT", dialCode: "+974", defaultAmount: 40 },
  { code: "OM", name: "Oman", currencyName: "Omani Rial", currency: "OMR", iso3: "OMN", dialCode: "+968", defaultAmount: 5 },
  { code: "BH", name: "Bahrain", currencyName: "Bahraini Dinar", currency: "BHD", iso3: "BHR", dialCode: "+973", defaultAmount: 5 },
  { code: "KW", name: "Kuwait", currencyName: "Kuwaiti Dinar", currency: "KWD", iso3: "KWT", dialCode: "+965", defaultAmount: 5 },
].map((c, i) => ({ ...c, shortName: c.name, isActive: true, sortOrder: i + 1 }));

// Older records use these spellings.
const ALIASES: Record<string, string> = {
  "united arab emirates": "AE",
  uae: "AE",
  ksa: "SA",
};

// Records created before the country field existed are UAE / AED.
export const DEFAULT_COUNTRY = "AE";
export const DEFAULT_CURRENCY = "AED";

// Live list, active and inactive. Mutated in place by setCountries() so
// every importer sees reloads.
export const COUNTRIES: Country[] = FALLBACK_COUNTRIES.map((c) => ({ ...c }));

export const countryFromDto = (row: CountryDto): Country => ({
  id: row.id,
  code: String(row.code).toUpperCase(),
  name: row.name,
  shortName: row.name,
  currency: String(row.currency_code || "").toUpperCase(),
  currencyName: row.currency_name || "",
  iso3: String(row.iso3 || "").toUpperCase(),
  dialCode: row.dial_code,
  defaultAmount: Number(row.default_amount ?? 0),
  isActive: row.is_active !== false,
  sortOrder: Number(row.sort_order ?? 0),
  regionCode: row.region_code ? String(row.region_code).toLowerCase() : null,
});

export const setCountries = (rows: CountryDto[]) => {
  if (!Array.isArray(rows) || !rows.length) return;
  COUNTRIES.splice(0, COUNTRIES.length, ...rows.map(countryFromDto));
};

// Countries offered for new registrations.
export const activeCountries = (): Country[] =>
  COUNTRIES.filter((c) => c.isActive);

export const normalizeCountry = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const v = String(value).trim().toLowerCase();
  if (!v) return null;
  const hit = COUNTRIES.find(
    (c) => c.code.toLowerCase() === v || c.name.toLowerCase() === v
  );
  if (hit) return hit.code;
  const alias = ALIASES[v];
  return alias && COUNTRIES.some((c) => c.code === alias) ? alias : null;
};

export const getCountry = (code?: string | null): Country =>
  COUNTRIES.find((c) => c.code === normalizeCountry(code)) ||
  COUNTRIES.find((c) => c.code === DEFAULT_COUNTRY) ||
  (FALLBACK_COUNTRIES[0] as Country);

// Display name for a stored country: "UAE", "Others", the raw value when it
// is not configured, or "" when none is saved (no default is assumed).
export const countryLabel = (value?: string | null): string => {
  const v = String(value ?? "").trim();
  if (!v) return "";
  if (/^others?$/i.test(v)) return "Others";
  const code = normalizeCountry(v);
  return COUNTRIES.find((c) => c.code === code)?.name || v;
};

export const currencyForCountry = (code?: string | null): string =>
  getCountry(code).currency;

type PricedPlan = {
  plan_name?: string;
  price: number | string;
  country_prices?:
    | Record<string, { currency?: string; price: number | string }>
    | string
    | null;
};

const parseCountryPrices = (raw: PricedPlan["country_prices"]) => {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

export type PlanPrice = { amount: string | null; currency: string; missing?: boolean };

// Same rule as the backend's resolvePlanPrice:
//   1. the plan's explicit price for the country;
//   2. free plans stay free;
//   3. AED countries pay the plan's base price;
//   4. the country's default amount (E-Paper Settings), when above 0;
//   5. otherwise no price — `missing`, and the backend refuses the request.
export const resolvePlanPrice = (
  plan: PricedPlan | null | undefined,
  countryCode: string
): PlanPrice | null => {
  if (!plan) return null;
  const country = getCountry(countryCode);
  const entry = parseCountryPrices(plan.country_prices)?.[country.code];
  if (entry && entry.price !== undefined && entry.price !== null && entry.price !== "") {
    const price = Number(entry.price);
    if (Number.isFinite(price) && price >= 0) {
      return {
        amount: String(price),
        currency: String(entry.currency || country.currency).toUpperCase(),
      };
    }
  }
  const base = Number(plan.price ?? 0);
  if (!(base > 0)) return { amount: "0", currency: country.currency };
  if (country.currency === DEFAULT_CURRENCY) {
    return { amount: String(base), currency: DEFAULT_CURRENCY };
  }
  if (country.defaultAmount > 0) {
    return { amount: String(country.defaultAmount), currency: country.currency };
  }
  return { amount: null, currency: country.currency, missing: true };
};

export const missingPriceMessage = (
  plan: PricedPlan | null | undefined,
  countryCode: string
): string =>
  `"${plan?.plan_name || "This plan"}" has no price for ${getCountry(countryCode).name}, and the country has no default amount. Set a plan price in Epaper Subscription Plans or a default amount in E-Paper Settings.`;

// "SAR 50" — falls back to AED for records saved before currency existed.
export const formatMoney = (
  amount: number | string | null | undefined,
  currency?: string | null
): string => `${currency || DEFAULT_CURRENCY} ${amount ?? ""}`.trim();

// Phone country codes come only from E-Paper Settings. These older codes
// are never offered in the dropdown; they are only used to split numbers
// saved before a country was configured, so editing such a record keeps its
// code instead of gluing it onto the local number.
const LEGACY_DIAL_CODES = ["+91", "+1", "+44", "+61", "+49", "+81", "+27", "+64", "+86"];

export type DialCodeOption = { code: string; label: string };

// "+91 India" — one option per dial code, active configured countries only.
// Pass the field's current value so a saved code (from a since-disabled
// country or a legacy record) still displays instead of a bare value.
export const dialCodeOptions = (current?: string | null): DialCodeOption[] => {
  const options: DialCodeOption[] = [];
  const seen = new Set<string>();
  for (const c of activeCountries()) {
    if (!c.dialCode || seen.has(c.dialCode)) continue;
    seen.add(c.dialCode);
    options.push({ code: c.dialCode, label: `${c.dialCode} ${c.name}` });
  }
  if (current && !seen.has(current)) {
    const inactive = COUNTRIES.find((c) => c.dialCode === current);
    options.push({
      code: current,
      label: inactive ? `${current} ${inactive.name} (inactive)` : current,
    });
  }
  return options;
};

// Every code a stored phone may start with, longest first (all configured
// countries, active or not, plus the legacy codes).
export const knownDialCodes = (): string[] =>
  Array.from(
    new Set([...COUNTRIES.map((c) => c.dialCode).filter(Boolean), ...LEGACY_DIAL_CODES])
  ).sort((a, b) => b.length - a.length);

// Splits a digits-only phone ("971501234567") into dial code + local number
// using the longest matching dial code. Returns null when no code matches
// (legacy numbers saved without a country code).
export const splitDigitsPhone = (
  value: string | number | null | undefined
): { dialCode: string; number: string } | null => {
  const digits = String(value ?? "").replace(/\D/g, "");
  const codes = knownDialCodes()
    .map((c) => c.replace(/\D/g, ""))
    .filter(Boolean);
  for (const code of codes) {
    if (digits.startsWith(code) && digits.length - code.length >= 7) {
      return { dialCode: `+${code}`, number: digits.slice(code.length) };
    }
  }
  return null;
};
